import { useQuery } from '@tanstack/react-query'
import { bridgeApi, type ComplianceAlert } from '@/lib/api'
import { normalizeComplianceAlert } from '@/lib/alert-utils'

export interface ComplianceAlertsResult {
  alerts: ComplianceAlert[]
  /** False when the product bridge is unreachable (alerts will be empty). */
  available: boolean
}

/**
 * Compliance alerts proxied from the TMAgent product API via the bridge. The
 * bridge returns `source: 'unavailable'` (and an empty list) when the product
 * API can't be reached — surfaced here as `available: false` so the UI can stay
 * quiet rather than implying "all clear".
 */
export function useComplianceAlerts() {
  return useQuery<ComplianceAlertsResult>({
    queryKey: ['compliance-alerts'],
    queryFn: async () => {
      const res = await bridgeApi.complianceAlerts()
      return {
        // Normalize at the data boundary: the bridge proxies an external API
        // whose records can omit fields the UI assumes always exist. One pass
        // here guarantees clean data for every downstream consumer.
        alerts: Array.isArray(res.data) ? res.data.map(normalizeComplianceAlert) : [],
        available: res.source !== 'unavailable',
      }
    },
    staleTime: 30_000,
  })
}
